import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { noteGoApiFetch } from '@/services/noteGoApi/noteGoApiFetcher';
import { useAuthStore } from '@/stores/authStore';
import type {
  OrganizationFolder,
  OrganizationFolderCreate,
  OrganizationFolderListResponse,
  OrganizationFolderNoteAdd,
  OrganizationFolderUpdate,
} from '../types';

type EmptyObject = Record<string, never>;

/**
 * Hook for managing an organization's folders.
 * Mirrors useOrganizations: query keys scoped by the current user's id so the
 * singleton React Query cache can't leak one user's folders into another user's
 * session, and all mutations invalidate the user+org scoped folders query.
 */
export function useOrganizationFolders(organizationId: string | undefined, enabled = true) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.userData?.id);

  const foldersQueryKey = ['organization-folders', userId, organizationId];

  const invalidateFolders = () => {
    queryClient.invalidateQueries({ queryKey: foldersQueryKey });
  };

  // Fetch all folders for the organization
  const {
    data: foldersData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: foldersQueryKey,
    queryFn: async () => {
      if (!organizationId) return { folders: [] as OrganizationFolder[] };
      const response = await noteGoApiFetch<
        OrganizationFolderListResponse,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}/folders',
        method: 'GET',
        pathParams: { organization_id: organizationId },
      });
      return response;
    },
    enabled: enabled && !!organizationId && !!userId,
    staleTime: 30000,
  });

  // Create folder mutation
  const createMutation = useMutation({
    mutationFn: async ({
      organizationId: orgId,
      name,
      access,
      memberEmails,
    }: {
      organizationId: string;
      name: string;
      access: OrganizationFolderCreate['access'];
      memberEmails?: string[];
    }) => {
      const body: OrganizationFolderCreate = { name, access };
      if (memberEmails) {
        body.member_emails = memberEmails;
      }
      const response = await noteGoApiFetch<
        OrganizationFolder,
        unknown,
        OrganizationFolderCreate,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}/folders',
        method: 'POST',
        body,
        pathParams: { organization_id: orgId },
      });
      return response;
    },
    onSuccess: invalidateFolders,
  });

  // Update folder mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      organizationId: orgId,
      folderId,
      name,
      access,
      memberEmails,
    }: {
      organizationId: string;
      folderId: string;
      name?: string;
      access?: OrganizationFolderUpdate['access'];
      memberEmails?: string[];
    }) => {
      const body: OrganizationFolderUpdate = {};
      if (name !== undefined) body.name = name;
      if (access !== undefined) body.access = access;
      if (memberEmails !== undefined) body.member_emails = memberEmails;
      const response = await noteGoApiFetch<
        OrganizationFolder,
        unknown,
        OrganizationFolderUpdate,
        EmptyObject,
        EmptyObject,
        { organization_id: string; folder_id: string }
      >({
        url: '/organizations/{organization_id}/folders/{folder_id}',
        method: 'PUT',
        body,
        pathParams: { organization_id: orgId, folder_id: folderId },
      });
      return response;
    },
    onSuccess: invalidateFolders,
  });

  // Delete folder mutation
  const deleteMutation = useMutation({
    mutationFn: async ({ organizationId: orgId, folderId }: { organizationId: string; folderId: string }) => {
      await noteGoApiFetch<
        void,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string; folder_id: string }
      >({
        url: '/organizations/{organization_id}/folders/{folder_id}',
        method: 'DELETE',
        pathParams: { organization_id: orgId, folder_id: folderId },
      });
      return folderId;
    },
    onSuccess: invalidateFolders,
  });

  // Add note to folder mutation
  const addNoteMutation = useMutation({
    mutationFn: async ({
      organizationId: orgId,
      folderId,
      noteId,
    }: {
      organizationId: string;
      folderId: string;
      noteId: string;
    }) => {
      const response = await noteGoApiFetch<
        OrganizationFolder,
        unknown,
        OrganizationFolderNoteAdd,
        EmptyObject,
        EmptyObject,
        { organization_id: string; folder_id: string }
      >({
        url: '/organizations/{organization_id}/folders/{folder_id}/notes',
        method: 'POST',
        body: { note_id: noteId },
        pathParams: { organization_id: orgId, folder_id: folderId },
      });
      return response;
    },
    onSuccess: invalidateFolders,
  });

  // Remove note from folder mutation
  const removeNoteMutation = useMutation({
    mutationFn: async ({
      organizationId: orgId,
      folderId,
      noteId,
    }: {
      organizationId: string;
      folderId: string;
      noteId: string;
    }) => {
      const response = await noteGoApiFetch<
        OrganizationFolder,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string; folder_id: string; note_id: string }
      >({
        url: '/organizations/{organization_id}/folders/{folder_id}/notes/{note_id}',
        method: 'DELETE',
        pathParams: { organization_id: orgId, folder_id: folderId, note_id: noteId },
      });
      return response;
    },
    onSuccess: invalidateFolders,
  });

  const folders = foldersData?.folders ?? [];
  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    addNoteMutation.isPending ||
    removeNoteMutation.isPending;

  return {
    folders,
    isLoading,
    isMutating,
    error,
    refetch,
    createFolder: createMutation.mutateAsync,
    updateFolder: updateMutation.mutateAsync,
    deleteFolder: deleteMutation.mutateAsync,
    addNoteToFolder: addNoteMutation.mutateAsync,
    removeNoteFromFolder: removeNoteMutation.mutateAsync,
  };
}
